import React, { useEffect, Suspense, lazy } from 'react';
import { Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { LoadingFallback, NavigateToMarketingRoot } from '../components/common';
import { getAppRoutes } from '../../app/index';
import ProtectedRoute from '../../components/auth/ProtectedRoute';
import { appConfigService } from '../../services/appConfig';

// Lazy-loaded pages (route-level code splitting)
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage'));
const VerifyEmailPage = lazy(() => import('../pages/auth/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPasswordPage'));
const RecoverAccountPage = lazy(() => import('../pages/auth/RecoverAccountPage'));
const OAuthCallbackPage = lazy(() => import('../pages/auth/OAuthCallbackPage'));
const BlogPage = lazy(() => import('../pages/public/BlogPage'));
const BlogPostPage = lazy(() => import('../pages/public/BlogPostPage'));
const GetStartedPage = lazy(() => import('../pages/public/GetStartedPage'));

const DashboardHomePage = lazy(() => import('../pages/user/DashboardHomePage'));
const ProfilePage = lazy(() => import('../pages/user/ProfilePage'));
const UserSettingsOverviewPage = lazy(() => import('../pages/user/UserSettingsOverviewPage'));
const UserProfileSettingsPage = lazy(() => import('../pages/user/settings/UserProfileSettingsPage'));
const UserPreferencesSettingsPage = lazy(() => import('../pages/user/settings/UserPreferencesSettingsPage'));
const UserNotificationsSettingsPage = lazy(() => import('../pages/user/settings/UserNotificationsSettingsPage'));
const ManagePage = lazy(() => import('../pages/manager/ManagePage'));
const ManageSettingsOverviewPage = lazy(() => import('../pages/manage/ManageSettingsOverviewPage'));
const ManagerContentSettingsPage = lazy(() => import('../pages/manage/settings/ManagerContentSettingsPage'));
const ManagerRealmSettingsPage = lazy(() => import('../pages/manage/settings/ManagerRealmSettingsPage'));
const ManagerUserSettingsPage = lazy(() => import('../pages/manage/settings/ManagerUserSettingsPage'));
const ManageProductsPage = lazy(() => import('../pages/manager/ManageProductsPage'));
const CreateProductPage = lazy(() => import('../pages/manager/CreateProductPage'));
const EditProductPage = lazy(() => import('../pages/manager/EditProductPage'));
const ManageCategoriesPage = lazy(() => import('../pages/manager/ManageCategoriesPage'));
const CreateCategoryPage = lazy(() => import('../pages/manager/CreateCategoryPage'));
const EditCategoryPage = lazy(() => import('../pages/manager/EditCategoryPage'));
const ManagePricingPage = lazy(() => import('../pages/manager/ManagePricingPage'));
const CreatePricePage = lazy(() => import('../pages/manager/CreatePricePage'));
const EditPricePage = lazy(() => import('../pages/manager/EditPricePage'));
const ManageMediaPage = lazy(() => import('../pages/manager/ManageMediaPage'));
const UploadMediaPage = lazy(() => import('../pages/manager/UploadMediaPage'));
const EditMediaPage = lazy(() => import('../pages/manager/EditMediaPage'));
const ManageExchangeRatePage = lazy(() => import('../pages/manager/ManageExchangeRatePage'));
const EditExchangeRatePage = lazy(() => import('../pages/manager/EditExchangeRatePage'));
const ManageAiUsage = lazy(() => import('../pages/manager/ManageAiUsage'));
const JobsListPage = lazy(() => import('../pages/user/JobsListPage'));
const AdminPage = lazy(() => import('../pages/admin/AdminPage'));
const AdminUserAccountsPage = lazy(() => import('../pages/admin/AdminUserAccountsPage'));
const AdminUserDetailsPage = lazy(() => import('../pages/admin/AdminUserDetailsPage'));
const CreateUserPage = lazy(() => import('../pages/admin/CreateUserPage'));
const EditUserPage = lazy(() => import('../pages/admin/EditUserPage'));
const AddRolePage = lazy(() => import('../pages/admin/AddRolePage'));
const GroupMembersPage = lazy(() => import('../pages/admin/GroupMembersPage'));
const AdminRealmsGroupsRolesPage = lazy(() => import('../pages/admin/AdminRealmsGroupsRolesPage'));
const AdminSettingsOverviewPage = lazy(() => import('../pages/admin/AdminSettingsOverviewPage'));
const AdminSettingsPage = lazy(() => import('../pages/admin/AdminSettingsPage'));
const CommunicationSettingsPage = lazy(() => import('../pages/admin/settings/CommunicationSettingsPage'));
const SecuritySettingsPage = lazy(() => import('../pages/admin/settings/SecuritySettingsPage'));
const AISettingsPage = lazy(() => import('../pages/admin/settings/AISettingsPage'));
const StorageSettingsPage = lazy(() => import('../pages/admin/settings/StorageSettingsPage'));
const FeaturesSettingsPage = lazy(() => import('../pages/admin/settings/FeaturesSettingsPage'));
const GeneralSettingsPage = lazy(() => import('../pages/admin/settings/GeneralSettingsPage'));
const AccessSettingsPage = lazy(() => import('../pages/admin/settings/AccessSettingsPage'));
const AdminBlogPage = lazy(() => import('../pages/admin/AdminBlogPage'));
const AdminGamificationPage = lazy(() => import('../pages/admin/AdminGamificationPage'));
const AdminUtilitiesPage = lazy(() => import('../pages/admin/AdminUtilitiesPage'));
const AdminAppTablesPage = lazy(() => import('../pages/admin/AdminAppTablesPage'));
const AdminLocalTablesPage = lazy(() => import('../pages/admin/AdminLocalTablesPage'));
const AdminSystemTablesPage = lazy(() => import('../pages/admin/AdminSystemTablesPage'));
const ServerHealthPage = lazy(() => import('../pages/admin/ServerHealthPage'));
const EditBlogPage = lazy(() => import('../pages/admin/EditBlogPage'));
const WebhookTestPage = lazy(() => import('../pages/admin/WebhookTestPage'));
const SSEMonitorPage = lazy(() => import('../pages/admin/SSEMonitorPage'));
const WebSocketMonitorPage = lazy(() => import('../pages/admin/WebSocketMonitorPage'));
const MCPAdminPage = lazy(() => import('../pages/admin/MCPAdminPage'));
const MCPServerFormPage = lazy(() => import('../pages/admin/MCPServerFormPage'));
const TablesPage = lazy(() => import('../pages/user/TablesPage'));
const TableEditorPage = lazy(() => import('../pages/user/TableEditorPage'));
const EditTableRowPage = lazy(() => import('../pages/user/EditTableRowPage'));
const ResetPasswordPage = lazy(() => import('../pages/user/ResetPasswordPage'));
const NotificationsPage = lazy(() => import('../pages/user/NotificationsPage'));
const OrderPage = lazy(() => import('../pages/user/OrderPage'));
const OrdersPage = lazy(() => import('../pages/user/OrdersPage'));
const ManageSubscriptionPage = lazy(() => import('../pages/user/ManageSubscriptionPage'));
const OrderCompletePage = lazy(() => import('../pages/user/OrderCompletePage'));
const OrderFailurePage = lazy(() => import('../pages/user/OrderFailurePage'));
const SiteBlogPage = lazy(() => import('../pages/user/SiteBlogPage'));
const SiteBlogEditPage = lazy(() => import('../pages/user/SiteBlogEditPage'));
const SiteManageProductsPage = lazy(() => import('../pages/user/SiteManageProductsPage'));
const SiteCreateProductPage = lazy(() => import('../pages/user/SiteCreateProductPage'));
const SiteEditProductPage = lazy(() => import('../pages/user/SiteEditProductPage'));
const SiteManageCategoriesPage = lazy(() => import('../pages/user/SiteManageCategoriesPage'));
const SiteCreateCategoryPage = lazy(() => import('../pages/user/SiteCreateCategoryPage'));
const SiteEditCategoryPage = lazy(() => import('../pages/user/SiteEditCategoryPage'));
const SiteManagePricingPage = lazy(() => import('../pages/user/SiteManagePricingPage'));
const SiteCreatePricePage = lazy(() => import('../pages/user/SiteCreatePricePage'));
const SiteEditPricePage = lazy(() => import('../pages/user/SiteEditPricePage'));

const SiteReportPage = lazy(() => import('../../app/pages/SiteReportPage'));
const CustomHome = lazy(() => import('../../app/home/CustomHome'));

// Wrapper component to check feature flag before rendering NotificationsPage
const NotificationsPageWrapper = () => {
    const isNotificationsEnabled = appConfigService.isFeatureEnabled('notifications');
    const navigate = useNavigate();

    useEffect(() => {
        if (!isNotificationsEnabled) {
            navigate('/home', { replace: true });
        }
    }, [isNotificationsEnabled, navigate]);

    if (!isNotificationsEnabled) {
        return null; // Will redirect, but return null during redirect
    }

    return <NotificationsPage />;
};

const AppRoutes = ({ LazyRoute }) => {
    // Generate custom app routes
    const appRoutes = getAppRoutes({ Route, LazyRoute, ProtectedRoute });

    return (
        <Routes>
            {/* Root: full page load to static marketing site (see 03-react-root-route.md) */}
            <Route path="/" element={<NavigateToMarketingRoot />} />
            {/* Public Routes - lazy loaded with Suspense */}
            <Route path="/login" element={<Suspense fallback={<LoadingFallback />}><LoginPage /></Suspense>} />
            <Route path="/register" element={<Suspense fallback={<LoadingFallback />}><RegisterPage /></Suspense>} />
            <Route path="/verifyEmail" element={<Suspense fallback={<LoadingFallback />}><VerifyEmailPage /></Suspense>} />
            <Route path="/forgotPassword" element={<Suspense fallback={<LoadingFallback />}><ForgotPasswordPage /></Suspense>} />
            <Route path="/recover-account" element={<Suspense fallback={<LoadingFallback />}><RecoverAccountPage /></Suspense>} />
            <Route path="/auth/callback" element={<Suspense fallback={<LoadingFallback />}><OAuthCallbackPage /></Suspense>} />
            <Route path="/blog" element={<Suspense fallback={<LoadingFallback />}><BlogPage /></Suspense>} />
            <Route path="/blog/:id" element={<Suspense fallback={<LoadingFallback />}><BlogPostPage /></Suspense>} />
            <Route path="/get-started" element={<Suspense fallback={<LoadingFallback />}><GetStartedPage /></Suspense>} />

            {/* Protected Routes - User Settings (under /home) */}
            <Route
                path="/home/settings"
                element={<LazyRoute element={<UserSettingsOverviewPage />} />}
            />
            <Route
                path="/home/settings/profile"
                element={<LazyRoute element={<UserProfileSettingsPage />} />}
            />
            <Route
                path="/home/settings/preferences"
                element={<LazyRoute element={<UserPreferencesSettingsPage />} />}
            />
            <Route
                path="/home/settings/notifications"
                element={<LazyRoute element={<UserNotificationsSettingsPage />} />}
            />

            {/* Custom App Home */}
            <Route
                path="/home"
                element={<LazyRoute element={<CustomHome />} />}
            />
            <Route
                path="/app/sites/:siteId/blog"
                element={<LazyRoute element={<SiteBlogPage />} />}
            />
            <Route
                path="/app/sites/:siteId/blog/new"
                element={<LazyRoute element={<SiteBlogEditPage />} />}
            />
            <Route
                path="/app/sites/:siteId/blog/:postId/edit"
                element={<LazyRoute element={<SiteBlogEditPage />} />}
            />
            <Route
                path="/app/sites/:siteId/report"
                element={<LazyRoute element={<SiteReportPage />} />}
            />

            {/* Site-scoped Product Management */}
            <Route
                path="/app/sites/:siteId/products"
                element={<LazyRoute element={<SiteManageProductsPage />} />}
            />
            <Route
                path="/app/sites/:siteId/products/create"
                element={<LazyRoute element={<SiteCreateProductPage />} />}
            />
            <Route
                path="/app/sites/:siteId/products/:productId/edit"
                element={<LazyRoute element={<SiteEditProductPage />} />}
            />
            <Route
                path="/app/sites/:siteId/categories"
                element={<LazyRoute element={<SiteManageCategoriesPage />} />}
            />
            <Route
                path="/app/sites/:siteId/categories/create"
                element={<LazyRoute element={<SiteCreateCategoryPage />} />}
            />
            <Route
                path="/app/sites/:siteId/categories/:categoryId/edit"
                element={<LazyRoute element={<SiteEditCategoryPage />} />}
            />
            <Route
                path="/app/sites/:siteId/pricing"
                element={<LazyRoute element={<SiteManagePricingPage />} />}
            />
            <Route
                path="/app/sites/:siteId/pricing/:productId/create"
                element={<LazyRoute element={<SiteCreatePricePage />} />}
            />
            <Route
                path="/app/sites/:siteId/pricing/:productId/:priceId/edit"
                element={<LazyRoute element={<SiteEditPricePage />} />}
            />

            {/* Notifications under /home */}
            <Route
                path="/home/notifications/*"
                element={<LazyRoute element={<NotificationsPageWrapper />} />}
            />

            {/* Notifications - direct route for header/navbar links */}
            <Route
                path="/notifications"
                element={<LazyRoute element={<NotificationsPageWrapper />} />}
            />

            {/* Profile under /home */}
            <Route
                path="/home/profile"
                element={<LazyRoute element={<ProfilePage />} />}
            />

            {/* Dashboard with all dashboard features */}
            <Route
                path="/dashboard/*"
                element={<LazyRoute element={<DashboardHomePage />} />}
            />

            {/* Redirects for backward compatibility - old /home/* routes to /dashboard/* */}
            <Route path="/home/projects" element={<Navigate to="/dashboard/projects" replace />} />
            <Route path="/home/projects/:id" element={<Navigate to="/dashboard/projects/:id" replace />} />
            <Route path="/home/ai-chat" element={<Navigate to="/dashboard/ai-chat" replace />} />
            <Route path="/home/ai-chat-history" element={<Navigate to="/dashboard/ai-chat-history" replace />} />
            <Route path="/home/ai-usage" element={<Navigate to="/dashboard/ai-usage" replace />} />
            <Route path="/home/orders" element={<Navigate to="/dashboard/orders" replace />} />
            <Route path="/home/order" element={<Navigate to="/dashboard/order" replace />} />
            <Route path="/home/assets" element={<Navigate to="/dashboard/assets" replace />} />
            <Route path="/home/local-data" element={<Navigate to="/dashboard/local-data" replace />} />
            <Route path="/home/sync-control" element={<Navigate to="/dashboard/sync-control" replace />} />
            <Route path="/home/circles-of-trust" element={<Navigate to="/dashboard/circles-of-trust" replace />} />
            <Route path="/home/jobs" element={<Navigate to="/dashboard/jobs" replace />} />
            <Route path="/home/jobs/:id" element={<Navigate to="/dashboard/jobs/:id" replace />} />
            <Route path="/home/mcp" element={<Navigate to="/dashboard/mcp" replace />} />
            <Route path="/home/mcp/:id" element={<Navigate to="/dashboard/mcp/:id" replace />} />

            <Route
                path="/profile"
                element={<LazyRoute element={<ProfilePage />} />}
            />

            {/* Manage Routes - handled by ManagePage Dashboard */}
            <Route
                path="/manage/*"
                element={<LazyRoute element={<ManagePage />} requiredRole={30} />}
            />

            {/* Admin Routes - handled by AdminPage Dashboard */}
            <Route
                path="/admin/*"
                element={<LazyRoute element={<AdminPage />} requiredRole={40} />}
            />

            {/* Table Editor Routes - used by admin tables */}
            <Route
                path="/table-editor/:tableType/:tableName"
                element={<LazyRoute element={<TableEditorPage />} requiredRole={30} />}
            />
            <Route
                path="/table-editor/:tableType/:tableName/new"
                element={<LazyRoute element={<EditTableRowPage />} requiredRole={30} />}
            />
            <Route
                path="/table-editor/:tableType/:tableName/:rowId/edit"
                element={<LazyRoute element={<EditTableRowPage />} requiredRole={30} />}
            />
            <Route
                path="/table-editor/:tableType/:tableName/:rowId/reset-password"
                element={<LazyRoute element={<ResetPasswordPage />} requiredRole={30} />}
            />

            {/* Order Routes */}
            <Route
                path="/order"
                element={<LazyRoute element={<OrderPage />} />}
            />
            <Route
                path="/orders"
                element={<LazyRoute element={<OrdersPage />} />}
            />
            <Route
                path="/order/complete"
                element={<LazyRoute element={<OrderCompletePage />} />}
            />
            <Route
                path="/order/failure"
                element={<LazyRoute element={<OrderFailurePage />} />}
            />

            {/* Purchase redirect - uses existing order page for MVP */}
            <Route path="/purchase" element={<Navigate to="/order" replace />} />

            {/* Password Reset - public entry point from email links */}
            <Route
                path="/reset-password"
                element={<Suspense fallback={<LoadingFallback />}><ResetPasswordPage /></Suspense>}
            />
            {/* Custom App Routes */}
            {appRoutes}

        </Routes>
    );
};

export default AppRoutes;
